import React, { useState, useEffect, useReducer, useCallback } from 'react';
import { Container, Card, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import MemoTextoOcorrencia from '~/Components/Ocorrencias/MemoTextoOcorrencia';
import ModalFiltrarTitulos from '~/Components/Ocorrencias/ModalFiltrarTitulos';
import FiltrosOcorrenciasContas from './components/FiltrosOcorrenciasContas';
import GridTitulos from './components/GridTitulos';
import GridClientes from './components/GridClientes';
import GridDatas from './components/GridDatas';
import PainelClienteTexto from './components/PainelClienteTexto';
import ModalBuscaContas from './components/ModalBuscaContas';
import ModalSituacaoClienteStub from './components/ModalSituacaoClienteStub';
import * as api from '~/Services/Service';

const TAMANHO_PAGINA = 50;

const hoje = moment().format('YYYY-MM-DD');
const mesPassado = moment().subtract(30, 'days').format('YYYY-MM-DD');

const estadoInicial = {
  // filtros
  filtros: {
    codigo_grupo_empresa: null,
    data_ini: mesPassado,
    data_fim: hoje,
    codigo_familia: null,
    codigo_usuario_filtro: null,
    somente_usuarios_ativos: true,
    texto: '',
    codigo_conta: null,
    codigo_conta_label: null,
    detalhar: false,
  },
  // grids
  titulos: { itens: [], total: 0, pagina: 1 },
  clientes: { itens: [], total: 0, pagina: 1 },
  datas: { itens: [], total: 0, pagina: 1 },
  textoAtual: null,
  // seleções
  tituloSelecionado: null,
  clienteSelecionado: null,
  dataSelecionada: null,
  titulosSel: {},
  // modo cliente/texto
  modoClienteTexto: false,
  clienteTexto: { itens: [], total: 0, pagina: 1 },
  // filtro de títulos (modal)
  titulosFiltro: { modo: 'IN', lista: [] },
  // lookups
  opcoesGE: [],
  opcoesEmpreendimento: [],
  opcoesUsuario: [],
  usuarioSemGe: false,
  // UI
  loadingLookups: false,
  loadingTitulos: false,
  loadingClientes: false,
  loadingDatas: false,
  loadingTexto: false,
  loadingClienteTexto: false,
  modalFiltrarAberto: false,
  modalBuscaContasAberto: false,
  modalSituacaoAberto: false,
  contaStub: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_FILTROS':
      return { ...state, filtros: action.payload };
    case 'SET_LOOKUPS':
      return { ...state, ...action.payload };
    case 'SET_TITULOS':
      return { ...state, titulos: action.payload };
    case 'SET_CLIENTES':
      return { ...state, clientes: action.payload };
    case 'SET_DATAS':
      return { ...state, datas: action.payload };
    case 'SET_TEXTO':
      return { ...state, textoAtual: action.payload };
    case 'SET_TITULO_SEL':
      return {
        ...state,
        tituloSelecionado: action.payload,
        clientes: { itens: [], total: 0, pagina: 1 },
        datas: { itens: [], total: 0, pagina: 1 },
        textoAtual: null,
        clienteSelecionado: null,
        dataSelecionada: null,
      };
    case 'SET_CLIENTE_SEL':
      return {
        ...state,
        clienteSelecionado: action.payload,
        datas: { itens: [], total: 0, pagina: 1 },
        textoAtual: null,
        dataSelecionada: null,
      };
    case 'SET_DATA_SEL':
      return { ...state, dataSelecionada: action.payload, textoAtual: null };
    case 'SEL_TOGGLE': {
      const chave = action.payload;
      return {
        ...state,
        titulosSel: {
          ...state.titulosSel,
          [chave]: !state.titulosSel[chave],
        },
      };
    }
    case 'MARCAR_TODOS': {
      const all = {};
      (state.titulos.itens || []).forEach((t) => { all[t.titulo] = true; });
      return { ...state, titulosSel: all };
    }
    case 'DESMARCAR_TODOS':
      return { ...state, titulosSel: {} };
    case 'RESET_SEL':
      return { ...state, titulosSel: {} };
    case 'SET_MODO_CLIENTE_TEXTO':
      return { ...state, modoClienteTexto: action.payload };
    case 'SET_CLIENTE_TEXTO':
      return { ...state, clienteTexto: action.payload };
    case 'SET_TITULOS_FILTRO':
      return { ...state, titulosFiltro: action.payload };
    case 'MODAL_FILTRAR':
      return { ...state, modalFiltrarAberto: action.payload };
    case 'MODAL_BUSCA_CONTAS':
      return { ...state, modalBuscaContasAberto: action.payload };
    case 'MODAL_SITUACAO':
      return { ...state, modalSituacaoAberto: action.payload, contaStub: action.conta };
    case 'SET_LOADING':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const ConsultaOcorrenciasContas = () => {
  const [state, dispatch] = useReducer(reducer, estadoInicial);
  const [titulosDisponiveis, setTitulosDisponiveis] = useState([]);

  // ── Lookups no mount ──────────────────────────────────────────────────────
  useEffect(() => {
    carregarLookups();
  }, []);

  const carregarLookups = async () => {
    dispatch({ type: 'SET_LOADING', payload: { loadingLookups: true } });
    try {
      const geResp = await api.consultaOcorrenciasContasLookupGeUsuario();
      const geUsuario = geResp || {};

      if (geUsuario.usuario_sem_ge === true) {
        dispatch({
          type: 'SET_LOOKUPS',
          payload: { usuarioSemGe: true, opcoesGE: [], loadingLookups: false },
        });
        return;
      }

      const opcoesGE = (geUsuario.ges_autorizados || []).map((g) => ({
        value: g.codigo,
        label: `${g.codigo} - ${g.apelido}`,
      }));

      const geDefault = geUsuario.ge_default ? geUsuario.ge_default : null;

      dispatch({
        type: 'SET_FILTROS',
        payload: { ...state.filtros, codigo_grupo_empresa: geDefault },
      });

      // Empreendimentos
      let opcoesEmpreendimento = [];
      try {
        const famResp = await api.consultaOcorrenciasContasLookupFamilias();
        opcoesEmpreendimento = (famResp || []).map((f) => ({
          value: f.codigo_familia,
          label: `${f.codigo_familia} - ${f.descricao}`,
        }));
      } catch (e) { /* ignora */ }

      // Usuários
      let opcoesUsuario = [];
      try {
        const usuResp = await api.consultaOcorrenciasContasLookupUsuarios({ somenteAtivos: true });
        opcoesUsuario = (usuResp || []).map((u) => ({
          value: u.codigo_usuario,
          label: `${u.codigo_usuario} - ${u.nome_usuario}`,
        }));
      } catch (e) { /* ignora */ }

      // W-0488 fix freeze: NAO carregamos mais a lista completa de contas aqui.
      // A selecao de Cliente passou a ser via ModalBuscaContas (busca server-side
      // com termo), evitando renderizar centenas de milhares de opcoes no combo.

      dispatch({
        type: 'SET_LOOKUPS',
        payload: {
          opcoesGE,
          opcoesEmpreendimento,
          opcoesUsuario,
          usuarioSemGe: false,
          loadingLookups: false,
        },
      });
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao carregar dados';
      toast.error(msg);
      dispatch({ type: 'SET_LOADING', payload: { loadingLookups: false } });
    }
  };

  // ── Recarregar usuários quando somente_usuarios_ativos muda ──────────────
  useEffect(() => {
    const carregarUsuarios = async () => {
      try {
        const usuResp = await api.consultaOcorrenciasContasLookupUsuarios({
          somenteAtivos: state.filtros.somente_usuarios_ativos,
        });
        const opcoesUsuario = (usuResp || []).map((u) => ({
          value: u.codigo_usuario,
          label: `${u.codigo_usuario} - ${u.nome_usuario}`,
        }));
        dispatch({ type: 'SET_LOOKUPS', payload: { opcoesUsuario } });
      } catch (e) { /* ignora */ }
    };
    if (!state.loadingLookups) carregarUsuarios();
  }, [state.filtros.somente_usuarios_ativos]);

  // ── Consultar títulos ─────────────────────────────────────────────────────
  const consultar = useCallback(async (pagina) => {
    if (!state.filtros.data_ini || !state.filtros.data_fim) {
      toast.warn('Período inicial e final são obrigatórios');
      return;
    }
    if (state.filtros.data_fim < state.filtros.data_ini) {
      toast.warn('Data final não pode ser anterior à inicial');
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: { loadingTitulos: true } });
    dispatch({ type: 'RESET_SEL' });
    try {
      const body = montarBody(pagina || 1);
      const resp = await api.consultaOcorrenciasContasTitulos(body);
      dispatch({
        type: 'SET_TITULOS',
        payload: {
          itens: resp && resp.itens ? resp.itens : [],
          total: resp && resp.total ? resp.total : 0,
          pagina: pagina || 1,
        },
      });
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao consultar títulos';
      toast.error(msg);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { loadingTitulos: false } });
    }
  }, [state.filtros, state.titulosFiltro]);

  const montarBody = (pagina) => {
    const f = state.filtros;
    const body = {
      codigo_grupo_empresa: f.codigo_grupo_empresa || null,
      data_ini: f.data_ini,
      data_fim: f.data_fim,
      codigo_familia: f.codigo_familia || null,
      codigo_usuario_filtro: f.codigo_usuario_filtro || null,
      somente_usuarios_ativos: f.somente_usuarios_ativos !== false,
      texto: f.texto || null,
      codigo_conta: f.codigo_conta || null,
      detalhar: f.detalhar === true,
      pagina: pagina || 1,
      tamanho_pagina: TAMANHO_PAGINA,
    };
    if (state.titulosFiltro && state.titulosFiltro.lista && state.titulosFiltro.lista.length > 0) {
      if (state.titulosFiltro.modo === 'IN') {
        body.titulos_in = state.titulosFiltro.lista;
      } else {
        body.titulos_not_in = state.titulosFiltro.lista;
      }
    }
    return body;
  };

  // ── Drill-down: Clientes ──────────────────────────────────────────────────
  useEffect(() => {
    if (!state.tituloSelecionado) return;
    const buscar = async () => {
      dispatch({ type: 'SET_LOADING', payload: { loadingClientes: true } });
      try {
        const body = {
          ...montarBody(1),
          titulo: state.tituloSelecionado,
          pagina: 1,
          tamanho_pagina: TAMANHO_PAGINA,
        };
        const resp = await api.consultaOcorrenciasContasClientes(body);
        dispatch({
          type: 'SET_CLIENTES',
          payload: {
            itens: resp && resp.itens ? resp.itens : [],
            total: resp && resp.total ? resp.total : 0,
            pagina: 1,
          },
        });
      } catch (err) {
        const msg = err && err.message ? err.message : 'Erro ao carregar clientes';
        toast.error(msg);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: { loadingClientes: false } });
      }
    };
    buscar();
  }, [state.tituloSelecionado]);

  // ── Drill-down: Datas ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!state.clienteSelecionado || !state.tituloSelecionado) return;
    const buscar = async () => {
      dispatch({ type: 'SET_LOADING', payload: { loadingDatas: true } });
      try {
        const body = {
          ...montarBody(1),
          titulo: state.tituloSelecionado,
          codigo_conta: state.clienteSelecionado.codigo_conta,
          codigo_grupo_empresa: state.clienteSelecionado.codigo_grupo_empresa,
          pagina: 1,
          tamanho_pagina: TAMANHO_PAGINA,
        };
        const resp = await api.consultaOcorrenciasContasDatas(body);
        dispatch({
          type: 'SET_DATAS',
          payload: {
            itens: resp && resp.itens ? resp.itens : [],
            total: resp && resp.total ? resp.total : 0,
            pagina: 1,
          },
        });
      } catch (err) {
        const msg = err && err.message ? err.message : 'Erro ao carregar datas';
        toast.error(msg);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: { loadingDatas: false } });
      }
    };
    buscar();
  }, [state.clienteSelecionado]);

  // ── Drill-down: Texto ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!state.dataSelecionada || !state.clienteSelecionado || !state.tituloSelecionado) return;
    const buscar = async () => {
      dispatch({ type: 'SET_LOADING', payload: { loadingTexto: true } });
      try {
        // GET com [FromUri]: o binder usa os nomes dos parametros da action C#
        // (camelCase) e NAO aplica o snake_case global do formatter de body.
        const params = {
          codigoConta: state.clienteSelecionado.codigo_conta,
          codigoGrupoEmpresa: state.clienteSelecionado.codigo_grupo_empresa,
          titulo: state.tituloSelecionado,
          dataOcorrencia: state.dataSelecionada,
        };
        const resp = await api.consultaOcorrenciasContasTexto(params);
        dispatch({ type: 'SET_TEXTO', payload: resp || null });
      } catch (err) {
        const msg = err && err.message ? err.message : 'Erro ao carregar texto';
        toast.error(msg);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: { loadingTexto: false } });
      }
    };
    buscar();
  }, [state.dataSelecionada]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleConsultar = () => consultar(1);

  const handleLimpar = () => {
    dispatch({ type: 'SET_FILTROS', payload: { ...estadoInicial.filtros } });
    dispatch({ type: 'SET_TITULOS', payload: { itens: [], total: 0, pagina: 1 } });
    dispatch({ type: 'SET_CLIENTES', payload: { itens: [], total: 0, pagina: 1 } });
    dispatch({ type: 'SET_DATAS', payload: { itens: [], total: 0, pagina: 1 } });
    dispatch({ type: 'SET_TEXTO', payload: null });
    dispatch({ type: 'SET_TITULO_SEL', payload: null });
    dispatch({ type: 'RESET_SEL' });
    dispatch({ type: 'SET_TITULOS_FILTRO', payload: { modo: 'IN', lista: [] } });
    dispatch({ type: 'SET_MODO_CLIENTE_TEXTO', payload: false });
  };

  const handleClienteTexto = async () => {
    const f = state.filtros;
    if (!f.codigo_conta && !(f.texto && f.texto.trim())) {
      toast.warn('Escolha um cliente ou informe um texto!');
      return;
    }
    dispatch({ type: 'SET_MODO_CLIENTE_TEXTO', payload: true });
    buscarClienteTexto(1);
  };

  const buscarClienteTexto = async (pagina) => {
    dispatch({ type: 'SET_LOADING', payload: { loadingClienteTexto: true } });
    try {
      const body = {
        ...montarBody(pagina),
        pagina,
        tamanho_pagina: TAMANHO_PAGINA,
      };
      const resp = await api.consultaOcorrenciasContasClienteTexto(body);
      dispatch({
        type: 'SET_CLIENTE_TEXTO',
        payload: {
          itens: resp && resp.itens ? resp.itens : [],
          total: resp && resp.total ? resp.total : 0,
          pagina,
        },
      });
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao buscar ocorrências';
      toast.error(msg);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { loadingClienteTexto: false } });
    }
  };

  const handleFecharClienteTexto = () => {
    dispatch({ type: 'SET_MODO_CLIENTE_TEXTO', payload: false });
  };

  const handleFiltrarTitulos = async () => {
    try {
      const body = { ...montarBody(1), pagina: 1, tamanho_pagina: 999 };
      const resp = await api.consultaOcorrenciasContasTitulosDisponiveis(body);
      setTitulosDisponiveis(Array.isArray(resp) ? resp : []);
    } catch (e) {
      setTitulosDisponiveis([]);
    }
    dispatch({ type: 'MODAL_FILTRAR', payload: true });
  };

  const handleConfirmarFiltroTitulos = (filtro) => {
    dispatch({ type: 'SET_TITULOS_FILTRO', payload: filtro });
    dispatch({ type: 'MODAL_FILTRAR', payload: false });
    consultar(1);
  };

  // ── Impressão ─────────────────────────────────────────────────────────────
  const handleImprimirTitulos = async (marcados) => {
    if (!marcados || marcados.length === 0) {
      toast.warn('Favor selecionar um Título');
      return;
    }
    let quebraPagina = false;
    if (marcados.length === 1) {
      quebraPagina = window.confirm('Deseja quebrar página por conta?');
    }
    try {
      const body = {
        ...montarBody(1),
        titulos_marcados: marcados,
        quebra_pagina_por_conta: quebraPagina,
      };
      const blob = await api.consultaOcorrenciasContasRelPorTitulos(body);
      abrirPdf(blob);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao gerar relatório';
      toast.error(msg);
    }
  };

  const handleImprimirCliente = async () => {
    try {
      const body = {
        ...montarBody(1),
        codigo_conta: state.clienteSelecionado ? state.clienteSelecionado.codigo_conta : null,
        codigo_grupo_empresa_cliente: state.clienteSelecionado ? state.clienteSelecionado.codigo_grupo_empresa : null,
      };
      const blob = await api.consultaOcorrenciasContasRelPorCliente(body);
      abrirPdf(blob);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao gerar relatório por cliente';
      toast.error(msg);
    }
  };

  const handleImprimirData = async () => {
    if (!state.dataSelecionada || !state.clienteSelecionado) {
      toast.warn('Selecione uma data no grid para imprimir');
      return;
    }
    try {
      const body = {
        codigo_conta: state.clienteSelecionado.codigo_conta,
        codigo_grupo_empresa: state.clienteSelecionado.codigo_grupo_empresa,
        titulo: state.tituloSelecionado,
        data_ocorrencia: state.dataSelecionada,
        codigo_familia: state.filtros.codigo_familia || null,
        codigo_usuario_filtro: state.filtros.codigo_usuario_filtro || null,
      };
      const blob = await api.consultaOcorrenciasContasRelPorData(body);
      abrirPdf(blob);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao gerar relatório por data';
      toast.error(msg);
    }
  };

  const handleImprimirListaCompleta = async () => {
    try {
      const body = montarBody(1);
      const blob = await api.consultaOcorrenciasContasRelListaCompleta(body);
      abrirPdf(blob);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao gerar lista completa';
      toast.error(msg);
    }
  };

  const abrirPdf = (blob) => {
    const url = window.URL.createObjectURL(blob);
    window.open(url);
  };

  // ── Paginação individual de cada grid ─────────────────────────────────────
  const handlePaginaTitulos = (pag) => consultar(pag);

  const handlePaginaClientes = async (pag) => {
    dispatch({ type: 'SET_LOADING', payload: { loadingClientes: true } });
    try {
      const body = {
        ...montarBody(pag),
        titulo: state.tituloSelecionado,
        pagina: pag,
        tamanho_pagina: TAMANHO_PAGINA,
      };
      const resp = await api.consultaOcorrenciasContasClientes(body);
      dispatch({
        type: 'SET_CLIENTES',
        payload: {
          itens: resp && resp.itens ? resp.itens : [],
          total: resp && resp.total ? resp.total : 0,
          pagina: pag,
        },
      });
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao paginar clientes';
      toast.error(msg);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { loadingClientes: false } });
    }
  };

  const handlePaginaDatas = async (pag) => {
    if (!state.clienteSelecionado) return;
    dispatch({ type: 'SET_LOADING', payload: { loadingDatas: true } });
    try {
      const body = {
        ...montarBody(pag),
        titulo: state.tituloSelecionado,
        codigo_conta: state.clienteSelecionado.codigo_conta,
        codigo_grupo_empresa: state.clienteSelecionado.codigo_grupo_empresa,
        pagina: pag,
        tamanho_pagina: TAMANHO_PAGINA,
      };
      const resp = await api.consultaOcorrenciasContasDatas(body);
      dispatch({
        type: 'SET_DATAS',
        payload: {
          itens: resp && resp.itens ? resp.itens : [],
          total: resp && resp.total ? resp.total : 0,
          pagina: pag,
        },
      });
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao paginar datas';
      toast.error(msg);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { loadingDatas: false } });
    }
  };

  const isLoading =
    state.loadingLookups ||
    state.loadingTitulos ||
    state.loadingClientes ||
    state.loadingDatas ||
    state.loadingTexto ||
    state.loadingClienteTexto;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Container fluid className="px-3 py-3">
      <Header title="Consulta de Ocorrências de Contas" />
      {isLoading && <Loading />}

      <FiltrosOcorrenciasContas
        filtros={state.filtros}
        setFiltros={(payload) =>
          dispatch({
            type: 'SET_FILTROS',
            payload:
              typeof payload === 'function' ? payload(state.filtros) : payload,
          })
        }
        onConsultar={handleConsultar}
        onLimpar={handleLimpar}
        onClienteTexto={handleClienteTexto}
        onFiltrarTitulos={handleFiltrarTitulos}
        onAbrirBuscaContas={() => dispatch({ type: 'MODAL_BUSCA_CONTAS', payload: true })}
        clienteSelecionadoLabel={state.filtros.codigo_conta_label}
        opcoesGE={state.opcoesGE}
        opcoesEmpreendimento={state.opcoesEmpreendimento}
        opcoesUsuario={state.opcoesUsuario}
        loadingLookups={state.loadingLookups}
        usuarioSemGe={state.usuarioSemGe}
        titulosFiltroAtivo={state.titulosFiltro && state.titulosFiltro.lista && state.titulosFiltro.lista.length > 0}
      />

      {state.modoClienteTexto ? (
        <PainelClienteTexto
          dados={state.clienteTexto.itens}
          total={state.clienteTexto.total}
          pagina={state.clienteTexto.pagina}
          onPaginaChange={buscarClienteTexto}
          onFechar={handleFecharClienteTexto}
          onImprimir={handleImprimirListaCompleta}
        />
      ) : (
        <Card className="shadow-sm mb-3">
          <Card.Body className="p-2">
            <Row>
              <Col md={4} className="mb-2">
                <GridTitulos
                  dados={state.titulos.itens}
                  total={state.titulos.total}
                  pagina={state.titulos.pagina}
                  titulosSel={state.titulosSel}
                  onSelToggle={(chave) => dispatch({ type: 'SEL_TOGGLE', payload: chave })}
                  onMarcarTodos={() => dispatch({ type: 'MARCAR_TODOS' })}
                  onDesmarcarTodos={() => dispatch({ type: 'DESMARCAR_TODOS' })}
                  onLinhaClick={(item) => dispatch({ type: 'SET_TITULO_SEL', payload: item.titulo })}
                  onImprimir={handleImprimirTitulos}
                  detalhar={state.filtros.detalhar}
                  onPaginaChange={handlePaginaTitulos}
                />
              </Col>
              <Col md={4} className="mb-2">
                <GridClientes
                  dados={state.clientes.itens}
                  total={state.clientes.total}
                  pagina={state.clientes.pagina}
                  clienteSelecionado={state.clienteSelecionado}
                  onLinhaClick={(item) =>
                    dispatch({
                      type: 'SET_CLIENTE_SEL',
                      payload: { codigo_conta: item.codigo_conta, codigo_grupo_empresa: item.codigo_grupo_empresa },
                    })
                  }
                  onLinhaDblClick={(item) =>
                    dispatch({
                      type: 'MODAL_SITUACAO',
                      payload: true,
                      conta: { codigo_conta: item.codigo_conta, codigo_grupo_empresa: item.codigo_grupo_empresa },
                    })
                  }
                  onImprimir={handleImprimirCliente}
                  onPaginaChange={handlePaginaClientes}
                />
              </Col>
              <Col md={4} className="mb-2">
                <GridDatas
                  dados={state.datas.itens}
                  total={state.datas.total}
                  pagina={state.datas.pagina}
                  dataSelecionada={state.dataSelecionada}
                  onLinhaClick={(item) =>
                    dispatch({ type: 'SET_DATA_SEL', payload: item.data_ocorrencia })
                  }
                  onImprimir={handleImprimirData}
                  onPaginaChange={handlePaginaDatas}
                />
              </Col>
            </Row>
            <MemoTextoOcorrencia
              texto={state.textoAtual ? state.textoAtual.texto_ocorrencia : null}
              usuario={state.textoAtual ? state.textoAtual.nome_usuario : null}
              data={state.dataSelecionada}
            />
          </Card.Body>
        </Card>
      )}

      <ModalFiltrarTitulos
        aberto={state.modalFiltrarAberto}
        onConfirmar={handleConfirmarFiltroTitulos}
        onCancelar={() => dispatch({ type: 'MODAL_FILTRAR', payload: false })}
        titulosDisponiveis={titulosDisponiveis}
        selecionadosInicial={state.titulosFiltro ? state.titulosFiltro.lista : []}
        modoInicial={state.titulosFiltro ? state.titulosFiltro.modo : 'IN'}
      />

      <ModalBuscaContas
        aberto={state.modalBuscaContasAberto}
        codigoGrupoEmpresa={state.filtros.codigo_grupo_empresa}
        onSelect={(c) => {
          dispatch({
            type: 'SET_FILTROS',
            payload: {
              ...state.filtros,
              codigo_conta: c.codigo_conta,
              codigo_conta_label: `${c.codigo_conta} - ${c.apelido_conta || ''}`,
            },
          });
          dispatch({ type: 'MODAL_BUSCA_CONTAS', payload: false });
        }}
        onFechar={() => dispatch({ type: 'MODAL_BUSCA_CONTAS', payload: false })}
      />

      <ModalSituacaoClienteStub
        aberto={state.modalSituacaoAberto}
        codigoConta={state.contaStub ? state.contaStub.codigo_conta : null}
        codigoGrupoEmpresa={state.contaStub ? state.contaStub.codigo_grupo_empresa : null}
        onFechar={() => dispatch({ type: 'MODAL_SITUACAO', payload: false, conta: null })}
      />
    </Container>
  );
};

export default ConsultaOcorrenciasContas;
